import React, { useEffect, useState, useRef } from "react";
import { Table, Input, Form, Col, Row } from "antd";
import { useDispatch, useSelector } from "react-redux";

import _ from "lodash";

import {
  fetchEnrollmentHistory,
  isAddingAction,
  setStorage,
} from "./redux/actions";

const HistoryTable = ({ canUpdate, canDelete }) => {
  const [editingLoading, setEditingLoading] = useState(false);
  const [editingKey, setEditingKey] = useState("");
  const [search, setSearch] = useState({});
  const [req, setReq] = useState(false);
  const [data, setData] = useState([]);
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const ref = useRef(null);
  const isEditing = (record) => record._id === editingKey;

  const { allHistory, fetchingLoading, updateLoading, isAdding, ...rest } =
    useSelector((state) => state.HistoryReducer);

  const [debouncedCallApi] = useState(() =>
    _.debounce(() => setReq((prev) => !prev), 500)
  );

  useEffect(() => {
    const localStorageExist = localStorage.getItem("historyManagerState");
    if (localStorageExist) {
      const localStorageData = JSON.parse(localStorageExist);

      if (localStorageData) {
        setTimeout(() => {
          setSearch(localStorageData.search);
          dispatch(setStorage(localStorageData));

          debouncedCallApi();
        }, 1000);
      }
    }
  }, []);

  useEffect(() => {
    dispatch(fetchEnrollmentHistory());

    return () => {
      dispatch(isAddingAction(false));
    };
  }, []);

  useEffect(() => {
    setEditingLoading(updateLoading);
    if (!updateLoading) {
      setEditingKey("");
    }
  }, [updateLoading]);

  useEffect(() => {
    setData(allHistory);
  }, [allHistory]);

  useEffect(() => {
    let filteredData = allHistory;

    if (search?.teacher?.length > 0) {
      filteredData = filteredData.filter((el) => {
        const fullName = `${el.enroled_by?.first_name ?? ""} ${
          el.enroled_by?.last_name ?? ""
        }`;
        return fullName.toLowerCase().includes(search.teacher.toLowerCase());
      });
    }

    if (search?.student?.length > 0) {
      filteredData = filteredData.filter((el) => {
        const fullName = `${el.enroled_user?.first_name ?? ""} ${
          el.enroled_user?.last_name ?? ""
        }`;
        return fullName.toLowerCase().includes(search.student.toLowerCase());
      });
    }

    if (search?.loginId?.length > 0) {
      filteredData = filteredData.filter((el) =>
        el.enroled_user?.login_id
          ?.toLowerCase()
          ?.includes(search.loginId.toLowerCase())
      );
    }

    if (search?.role?.length > 0) {
      filteredData = filteredData.filter((el) => {
        const role = el.enroled_user?.roles?.[0] ?? "";
        return role.toLowerCase().includes(search.role.toLowerCase());
      });
    }

    if (search?.class?.length > 0) {
      filteredData = filteredData.filter((el) => {
        const className = el.class?.name ?? "";
        const classCode = el.class?.code ?? "";
        const searchVal = search.class.toLowerCase();
        return (
          className.toLowerCase().includes(searchVal) ||
          classCode.toLowerCase().includes(searchVal)
        );
      });
    }

    if (search?.date?.length > 0) {
      filteredData = filteredData.filter((el) => {
        const dateStr = new Date(el.timestamps).toLocaleString();
        return dateStr.toLowerCase().includes(search.date.toLowerCase());
      });
    }

    if (search?.status?.length > 0) {
      filteredData = filteredData.filter((el) =>
        el.status?.toLowerCase()?.includes(search.status.toLowerCase())
      );
    }

    setData(filteredData);
  }, [req]);

  useEffect(() => {
    const isSearchChanged =
      JSON.stringify(search) !== JSON.stringify(rest.search);

    if (isSearchChanged) {
      setSearch(rest.search);
    }

    if (isSearchChanged) {
      debouncedCallApi();
    }
  }, []);

  const EditableCell = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    index,
    children,
    ...restProps
  }) => {
    const inputNode = <Input />;
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[{ required: true, message: `Please Input ${title}!` }]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const handleSetStorage = (key, value) => {
    dispatch(setStorage({ [key]: value }));
  };

  const handleSearch = (key, value, func = null) => {
    let updatedState = {
      ...search,
      [key]: value,
    };

    setSearch(updatedState);

    if (typeof func === "function") {
      func();
    }

    dispatch(setStorage({ search: updatedState }));
    handleSetStorage("search", updatedState);
  };

  const searchInput = (key) => (
    <Input
      onClick={(e) => e.stopPropagation()}
      value={search[key]}
      onChange={(e) => {
        handleSearch(key, e.target.value, debouncedCallApi);
      }}
    />
  );

  const columns = [
    {
      title: () => (
        <div align="top" style={{ width: "100%" }}>
          <div className="special-table-header-text">Enrol By</div>
          <div>{searchInput("teacher")}</div>
        </div>
      ),
      dataIndex: "enroled_by",
      key: "teacher",
      width: "15%",
      sorter: (a, b) =>
        `${a.enroled_by?.first_name ?? ""} ${a.enroled_by?.last_name ?? ""}`
          .toLowerCase()
          .localeCompare(
            `${b.enroled_by?.first_name ?? ""} ${
              b.enroled_by?.last_name ?? ""
            }`.toLowerCase()
          ),
      render: (teacher) => (
        <div style={{ paddingLeft: "12px" }}>
          {`${teacher?.first_name ?? ""} ${teacher?.last_name ?? ""}`}
        </div>
      ),
    },
    {
      title: () => (
        <div align="top" style={{ width: "100%" }}>
          <div className="special-table-header-text">Enrol To</div>
          <div>{searchInput("student")}</div>
        </div>
      ),
      dataIndex: "enroled_user",
      key: "student",
      width: "15%",
      sorter: (a, b) =>
        `${a.enroled_user?.first_name ?? ""} ${a.enroled_user?.last_name ?? ""}`
          .toLowerCase()
          .localeCompare(
            `${b.enroled_user?.first_name ?? ""} ${
              b.enroled_user?.last_name ?? ""
            }`.toLowerCase()
          ),
      render: (student) => (
        <div style={{ paddingLeft: "12px" }}>
          {`${student?.first_name ?? ""} ${student?.last_name ?? ""}`}
        </div>
      ),
    },
    {
      title: () => (
        <div align="top" style={{ width: "100%" }}>
          <div className="special-table-header-text">User Id</div>
          <div>{searchInput("loginId")}</div>
        </div>
      ),
      dataIndex: "enroled_user",
      key: "loginId",
      width: "18%",
      sorter: (a, b) =>
        (a.enroled_user?.login_id ?? "")
          .toLowerCase()
          .localeCompare((b.enroled_user?.login_id ?? "").toLowerCase()),
      render: (user) => (
        <div style={{ paddingLeft: "12px" }}>{user?.login_id ?? "-"}</div>
      ),
    },
    {
      title: () => (
        <div align="top" style={{ width: "100%" }}>
          <div className="special-table-header-text">Role</div>
          <div>{searchInput("role")}</div>
        </div>
      ),
      dataIndex: "enroled_user",
      key: "role",
      width: "12%",
      sorter: (a, b) =>
        (a.enroled_user?.roles?.[0] ?? "")
          .toLowerCase()
          .localeCompare((b.enroled_user?.roles?.[0] ?? "").toLowerCase()),
      render: (user) => (
        <div style={{ paddingLeft: "12px" }}>
          {user?.roles?.[0]?.toUpperCase() ?? "-"}
        </div>
      ),
    },
    {
      title: () => (
        <div align="top" style={{ width: "100%" }}>
          <div className="special-table-header-text">Class</div>
          <div>{searchInput("class")}</div>
        </div>
      ),
      dataIndex: "class",
      key: "class",
      width: "15%",
      sorter: (a, b) =>
        (a.class?.name ?? "")
          .toLowerCase()
          .localeCompare((b.class?.name ?? "").toLowerCase()),
      render: (cls) => (
        <div style={{ paddingLeft: "12px" }}>
          {cls?.name ? `${cls.name} (${cls.code})` : "-"}
        </div>
      ),
    },
    {
      title: () => (
        <div align="top" style={{ width: "100%" }}>
          <div className="special-table-header-text">Action</div>
          <div>{searchInput("status")}</div>
        </div>
      ),
      dataIndex: "status",
      key: "action",
      width: "10%",
      sorter: (a, b) => (a.status ?? "").localeCompare(b.status ?? ""),
      render: (status) => (
        <div style={{ paddingLeft: "12px", textTransform: "capitalize" }}>
          {status}
        </div>
      ),
    },
    {
      title: () => (
        <div align="top" style={{ width: "100%" }}>
          <div className="special-table-header-text">Date</div>
          <div>{searchInput("date")}</div>
        </div>
      ),
      dataIndex: "timestamps",
      key: "date",
      width: "15%",
      sorter: (a, b) => new Date(a.timestamps) - new Date(b.timestamps),
      render: (date) => (
        <div style={{ paddingLeft: "12px" }}>
          {new Date(date).toLocaleString()}
        </div>
      ),
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: "text",
        dataIndex: col.dataIndex,
        editing: isEditing(record),
      }),
    };
  });

  return (
    <div className="page-top-div">
      <span className="text-span">History</span>
      <Row justify="center" className="production-manager-parent-row">
        <Col xxl={24} xl={24} lg={24} md={24} className="heading-col">
          <Row align="middle" gutter={[30, 0]}>
            <Col>
              <div>
                <span className="heading-span">History Manager</span>
              </div>
            </Col>
          </Row>
        </Col>

        <Col lg={24} xl={24} xxl={24} md={24} style={{ minHeight: "70vh" }}>
          <Form form={form} component={false} className="table-style">
            <Table
              className="special-table activity-type-table project-statuses-table"
              components={{
                body: {
                  cell: EditableCell,
                },
              }}
              scroll={{ x: !fetchingLoading }}
              rowClassName="editable-row"
              dataSource={data}
              loading={fetchingLoading}
              columns={mergedColumns}
              rowKey="_id"
            />
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default HistoryTable;
